import { createHash } from "node:crypto";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { buildContactEmail } from "@/lib/email-templates";

const CONTACT_RATE_TTL_MS = 15 * 60 * 1000;
const CONTACT_MAX_PER_EMAIL = 5;
const CONTACT_MAX_PER_IP = 10;

const SubmitContactMessageInput = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().max(40).optional(),
  message: z.string().trim().min(1).max(5000),
  /** Honeypot: must be empty. Bots that fill it get a silent success. */
  website: z.string().max(200).optional(),
});

function hashIp(ip: string): string {
  return createHash("sha256").update(ip).digest("hex");
}

function clientIpFromRequest(): string | null {
  const request = getRequest();
  if (!request?.headers) return null;
  const nf = request.headers.get("x-nf-client-connection-ip")?.trim();
  if (nf) return nf;
  const xff = request.headers.get("x-forwarded-for")?.split(",")[0]?.trim();
  if (xff) return xff;
  const cf = request.headers.get("cf-connecting-ip")?.trim();
  if (cf) return cf;
  return null;
}

async function isContactRateLimited(email: string, ipHash: string | null): Promise<boolean> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const windowStart = new Date(Date.now() - CONTACT_RATE_TTL_MS).toISOString();

  const { count: emailCount, error: emailErr } = await supabaseAdmin
    .from("contact_form_rate_limits")
    .select("id", { count: "exact", head: true })
    .eq("email", email)
    .gte("created_at", windowStart);
  if (emailErr) throw new Error(emailErr.message);
  if ((emailCount ?? 0) >= CONTACT_MAX_PER_EMAIL) return true;

  if (ipHash) {
    const { count: ipCount, error: ipErr } = await supabaseAdmin
      .from("contact_form_rate_limits")
      .select("id", { count: "exact", head: true })
      .eq("ip_hash", ipHash)
      .gte("created_at", windowStart);
    if (ipErr) throw new Error(ipErr.message);
    if ((ipCount ?? 0) >= CONTACT_MAX_PER_IP) return true;
  }

  return false;
}

async function recordContactAttempt(email: string, ipHash: string | null): Promise<void> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { error } = await supabaseAdmin.from("contact_form_rate_limits").insert({
    email,
    ip_hash: ipHash,
  });
  if (error) throw new Error(error.message);
}

export const submitContactMessage = createServerFn({ method: "POST" })
  .validator((d) => SubmitContactMessageInput.parse(d))
  .handler(async ({ data }) => {
    if (data.website?.trim()) {
      return { ok: true as const };
    }

    const email = data.email.toLowerCase();
    const rawIp = clientIpFromRequest();
    const ipHash = rawIp ? hashIp(rawIp) : null;

    // Always return the same shape when limited so callers cannot probe limits.
    if (await isContactRateLimited(email, ipHash)) {
      return { ok: true as const };
    }

    await recordContactAttempt(email, ipHash);

    const payload = buildContactEmail({
      name: data.name,
      email,
      phone: data.phone,
      message: data.message,
    });

    try {
      const { sendEmail } = await import("@/lib/email.server");
      await sendEmail(payload);
    } catch (err) {
      console.error("[contact] send failed:", err instanceof Error ? err.message : err);
      throw new Error("Could not send your message. Please try again or call us.");
    }

    return { ok: true as const };
  });
